import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { produce } from 'immer';
import { actions } from '../../store/surveyTemplate';
import FormController from '../../common/formController';
import Joi from 'joi';
import validators from './validators';
import PermissionCheck from 'PermissionsCheck.js';
import { setCustomizedTemplate } from 'store/surveyTemplate/actions';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';
import { getEnvVar } from 'helpers';
import { shouldPreserveCopyTemplateState } from 'helpers/templateCopyGuard';

const {
  getSurveyTemplate,
  setCurrentSurveyTemplate,
  updateSurveyTemplate,
  createSurveyTemplate,
  testSurveyTemplate,
  templateAutoSaveTimerStart,
  setNextStep,
  unsetNextStep,
  setCopyCurrentSurveyTemplate
} = actions;

let formController = new FormController();

export default function useTemplate(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { templateUuid: originalTemplateUuid } = useParams();
  const [templateUuid, setTemplateUuid] = useState(originalTemplateUuid);
  const isAutoSaveDisabled = getEnvVar('ADM_DISABLE_AUTO_SAVE') === 'true';
  const templateAutoSaveTimerRunning = useSelector(store => store.surveyTemplate.isAutoSaveTimerRunning);
  const template = useSelector(store => store.surveyTemplate.current);
  const templateStarters = useSelector(store => store.surveyTemplate.starters);
  const isLoading = useSelector(store => store.surveyTemplate.getting);
  const isTestingTemplate = useSelector(
    store => store.surveyTemplate.isTesting
  );
  const isTestingSucceeded = useSelector(
    store => store.surveyTemplate.isTestingSucceeded
  );

  const isTemplateSaved = useSelector(
    store => store.surveyTemplate.isTemplateSaved
  );
  const shouldAutoSave = useSelector(
    store => store.surveyTemplate.shouldAutoSave
  );
  const isAutoSaveSuccess = useSelector(
    store => store.surveyTemplate.isAutoSaveSuccess
  );
  const isTemplateSaving = useSelector(
    store => store.surveyTemplate.isTemplateSaving
  );
  const isInTemplateDesignDialog = useSelector(
    store => store.surveyTemplate.isInTemplateDesignDialog
  );

  const supportedInternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedInternalSurveyResponseTypes
  );

  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );

  const permissions = PermissionCheck();
  const { hasPermission } = permissions;

  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [openTestTemplateModal, setOpenTestTemplateModal] = useState(false);

  const [thisTemplate, setThisTemplate] = useState({
    type: 'EMAIL',
    name: '',
    defaultTemplate: false,
    eventMetaData: []
  });

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(thisTemplate, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isLoading) {
      const queryParams = new URLSearchParams(location.search);
      const businessContextId = queryParams.get('businessContextId');
      
      dispatch(getSurveyTemplate({ 
        templateUuid,
        ...(businessContextId && { businessContextId })
      }));
    }
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return {
          ...template,
          type: prevTemplate.type
        };
      });
    }
  }, []);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return {
          ...template,
          type: prevTemplate.type
        };
      });
    }
    if (!templateUuid) {
      setThisTemplate(prevTemplate => {
        const queryParams = new URLSearchParams(location.search);
        const isCopyMode = queryParams.get('copy') === 'true';
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevTemplate,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevTemplate;
        }

        return {
          ...template,
          hasMovedToNextStep: template.hasMovedToNextStep || false,
          type: prevTemplate.type
        };
      });
      if (!template.productCategory) {
        autoPopulateProductDetails();
      }
    }
  }, [template]);

  useEffect(() => {
    if (
      !thisTemplate.productCategory &&
      thisTemplate.productType !== ProductType.List
    ) {
      autoPopulateProductDetails();
    }
  }, [thisTemplate.productType]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.List
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: listManagementSchema?.eventProductCategory || '',
        productName: listManagementSchema?.eventProductName || '',
        eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
        schemaIdentifier:
          listManagementSchema?.eventProductCategory +
            '.' +
            listManagementSchema?.eventProductName +
            '.' +
            listManagementSchema?.eventTypeCategory || ''
      });
    }
  }, [listManagementSchema]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    
    if (copy === 'true') {
      setTemplateUuid(undefined);

      let templateToCopy = Object.assign(
        {}, 
        template
      );
      if (templateToCopy && (templateToCopy.uuid || templateToCopy.id || templateToCopy.name)) {

        delete templateToCopy.id;
        delete templateToCopy.uuid;
        delete templateToCopy.defaultTemplate;
        templateToCopy._isCopy = true;
        templateToCopy._isCopyTemplate = true;
        templateToCopy.name = `${templateToCopy.name || ''}`.endsWith('-copy')
          ? `${templateToCopy.name || ''}`
          : `${templateToCopy.name || ''}-copy`;
        templateToCopy.active = true;
        templateToCopy.schemaIdentifier = templateToCopy.schemaIdentifier
          ? templateToCopy.schemaIdentifier
          : templateToCopy.productCategory
          ? `${templateToCopy.productCategory}.${templateToCopy.productName}.${templateToCopy.eventTypeCategory}`
          : '';
        // Update businessContextId for copied templates
        templateToCopy.businessContextId = currentBusinessContextId;

        setThisTemplate(prevTemplate => ({
          ...templateToCopy,
          hasMovedToNextStep: prevTemplate.hasMovedToNextStep || false
        }));

        const newUrl = '/surveyResultResponse-templates/details';
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, template.id, template.uuid, setCopyCurrentSurveyTemplate]);

  const autoPopulateProductDetails = () => {
    let options = supportedInternalSurveyResponseTypeOptions?.filter(
      option => option.type === 'EVENT_TYPE_CATEGORY'
    );
    if (options.length === 1) {
      handleAutomationChange({
        target: {
          name: 'schemaIdentifier',
          value: options[0]?.schemaIdentifier
        }
      });
    }
  };

  const resetValidationErrors = () => {
    setValidationErrors({});
    setThisTemplate({
      type: 'EMAIL',
      name: '',
      defaultTemplate: false
    });
  };

  const goNext = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentSurveyTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true,
          productType: thisTemplate.productType || ProductType.Product
        })
      );
      formController = new FormController();
      if (
        nextPath ||
        (event.currentTarget.attributes.link &&
          event.currentTarget.attributes.link.value)
      ) {
        history.push(
          `${nextPath || event.currentTarget.attributes.link.value}${
            (templateUuid && !(thisTemplate._isCopy || thisTemplate._isCopyTemplate)) ? `/${templateUuid}` : ''
          }`
        );
      } else {
        dispatch(setNextStep());
      }
    }
    startAutoSaveTimer();
  };

  const goBack = event => {
    dispatch(setCurrentSurveyTemplate({ ...thisTemplate }));
    if (
      backPath ||
      (event.currentTarget.attributes.link &&
        event.currentTarget.attributes.link.value)
    ) {
      history.push(
        `${backPath || event.currentTarget.attributes.link.value}${
          templateUuid ? `/${templateUuid}` : ''
        }`
      );
    } else {
      dispatch(unsetNextStep());
    }
  };

  const navBackOnError = path => {
    if (
      thisTemplate.hasOwnProperty('hasMovedToNextStep') &&
      !thisTemplate.hasMovedToNextStep &&
      (!thisTemplate.name || !thisTemplate.type)
    ) {
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentSurveyTemplate(thisTemplate));
  };

  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleChange = (events, isCustomizeTemplate) => {
    events = Array.isArray(events) ? events : [events];
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);

    let updatedTemplate = produce(thisTemplate, templateDraft => {
      if (events.length > 0) {
        events.forEach(event => {
          if (isCopyFlow && event?.target?.name === 'layout') {
            return;
          }
          setByPath(templateDraft, event.target.name, event.target.value);
        });
      }
    });

    setThisTemplate({ ...updatedTemplate, type: thisTemplate.type });
    if (isCustomizeTemplate) {
      dispatch(
        setCustomizedTemplate({ ...updatedTemplate, type: thisTemplate.type })
      );
    }
    validate(false);
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
    if (isCopyFlow && name === 'schemaIdentifier') {
      return;
    }
    let typeOption = {};
    let templateUpdate = produce(thisTemplate, draft => {
      if (supportedInternalSurveyResponseTypeOptions) {
        typeOption = supportedInternalSurveyResponseTypeOptions.find(
          option => option.id === value
        );

        draft.productCategory = typeOption?.productCategory || '';
        draft.productName = typeOption?.productName || '';
        draft.eventTypeCategory = typeOption?.eventTypeCategory || '';
        draft.schemaIdentifier = typeOption?.schemaIdentifier || '';
      }
      setByPath(draft, name, value);
    });
    setThisTemplate({ ...templateUpdate, type: thisTemplate.type });
    validate(false);
  };

  const handlePermissions = checkPermission => {
    return hasPermission(checkPermission);
  };

  const saveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
      dispatch(setCurrentSurveyTemplate({
        ...thisTemplate,
        ...(thisTemplate.productType === ProductType.List && {
          defaultTemplate: false
        })
      }));
      if (thisTemplate.uuid && !isCopyFlow) {
        dispatch(updateSurveyTemplate());
      } else {
        dispatch(createSurveyTemplate());
      }
      formController = new FormController();
    }
  };

  const closeTemplate = () => {
    formController = new FormController();
    history.push('/surveyResultResponse-templates');
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const validateTestTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      setOpenTestTemplateModal(true);
    } else {
      setOpenTestTemplateModal(false);
    }
  };

  const closeTestTemplateModal = () => setOpenTestTemplateModal(false);

  const getTestTemplate = value => {
    dispatch(
      setCurrentSurveyTemplate({
        ...thisTemplate,
        active: true,
        verificationRecipientInfo: value
      })
    );
    dispatch(testSurveyTemplate());
  };

  const startAutoSaveTimer = () => {
    if (template.tenantId === currentTenantId && template.businessContextId === currentBusinessContextId
      && !isLoading && !templateAutoSaveTimerRunning && !isAutoSaveDisabled
    ) {
      dispatch(templateAutoSaveTimerStart());
    }
  };

  const autoSaveTemplate = () => {
    const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);
    dispatch(setCurrentSurveyTemplate({ ...thisTemplate }));
    if (thisTemplate.uuid && !isCopyFlow) {
      dispatch(updateSurveyTemplate());
    } else {
      dispatch(createSurveyTemplate());
    }
    formController = new FormController();
  };

  const toggleDefaultTemplate = () => {
    setThisTemplate({
      ...thisTemplate,
      defaultTemplate: !thisTemplate.defaultTemplate
    });
  };

  const modifyTags = updatedTags => {
    setThisTemplate({
      ...thisTemplate,
      tags: [...updatedTags]
    });
  };

  const isTemplateReadOnly = isPopupDialog => {
    return (
      thisTemplate.uuid &&
      (currentTenantId !== thisTemplate.tenantId ||
        currentBusinessContextId !== thisTemplate.businessContextId) &&
      !isPopupDialog
    );
  };

  const showWarningMessage = () => {
    return (
      isDefaultTemplate() &&
      thisTemplate.tenantId === currentTenantId &&
      thisTemplate.businessContextId === currentBusinessContextId
    );
  };

  const isDefaultTemplate = () => {
    return thisTemplate.uuid && template.defaultTemplate;
  };

  const showDefaultCheckbox = () => {
    return (
      ((currentTenantId === '0' && currentBusinessContextId === 'Default') ||
        thisTemplate.defaultTemplate) && thisTemplate.productType !== ProductType.List
    );
  };

  const isTenantDefaultTemplate = () => {
    return (
      (!thisTemplate.defaultTemplate ||
        thisTemplate.tenantId === currentTenantId) &&
      thisTemplate.businessContextId !== currentBusinessContextId
    );
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisTemplate({
      ...thisTemplate,
      productType: event.target.value,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: ''
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisTemplate({
        ...thisTemplate,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisTemplate({
      ...thisTemplate,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  return {
    thisTemplate,
    templateStarters,
    supportedInternalSurveyResponseTypeOptions,
    validationErrors,
    openTestTemplateModal,
    closeTemplate,
    closeTestTemplateModal,
    handleChange,
    handleAutomationChange,
    handlePermissions,
    goNext,
    goBack,
    navBackOnError,
    onTabChange,
    saveTemplate,
    onFocus,
    onBlur,
    showFormSaveError,
    getErrorMessage,
    getTestTemplate,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    validateTestTemplate,
    autoSaveTemplate,
    startAutoSaveTimer,
    isLoading,
    shouldAutoSave,
    isAutoSaveSuccess,
    isTestingTemplate,
    isTestingSucceeded,
    isTemplateSaved,
    isTemplateSaving,
    isInTemplateDesignDialog,
    resetValidationErrors,
    toggleDefaultTemplate,
    modifyTags,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    isTenantDefaultTemplate,
    handleTypeChange,
    handleSubscriberListChange
  };
}
